import { useMutation } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { ApiError, api, readTableFile } from '../api/client.js'
import { Field, Modal, Notice } from './ui.js'

export interface ListImportResult {
  rows: number
  created: number
  updated: number
  unchanged: number
  warnings: string[]
  changes?: string[]
  dryRun: boolean
}

/** Accepted everywhere: Excel as it is, or CSV. */
export const TABLE_ACCEPT = '.xlsx,.csv,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * Import for a list that can also be exported: upload, check, then write.
 * Rows that match an existing entry are updated, the rest are created, and
 * nothing is written before "Jetzt importieren".
 */
export function ListImportModal({
  open,
  onClose,
  title,
  endpoint,
  help,
  nouns,
  onDone,
}: {
  open: boolean
  onClose: () => void
  title: string
  endpoint: string
  help: ReactNode
  /** e.g. ["Plan", "Pläne"] */
  nouns: [string, string]
  onDone?: () => void
}) {
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState('')
  const [reading, setReading] = useState(false)
  const [result, setResult] = useState<ListImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api<ListImportResult>(endpoint, { method: 'POST', body: { csv, dryRun } }),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
      if (!data.dryRun) onDone?.()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Import fehlgeschlagen.'),
  })

  function close() {
    setCsv('')
    setFileName('')
    setResult(null)
    setError(null)
    onClose()
  }

  async function pick(file: File | undefined) {
    if (!file) return
    setReading(true)
    setResult(null)
    setError(null)
    try {
      setCsv(await readTableFile(file))
      setFileName(file.name)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Die Datei konnte nicht gelesen werden.')
    } finally {
      setReading(false)
    }
  }

  const n = (count: number) => `${count} ${count === 1 ? nouns[0] : nouns[1]}`

  return (
    <Modal
      open={open}
      title={title}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={close}>
            {result && !result.dryRun ? 'Fertig' : 'Abbrechen'}
          </button>
          {!result && (
            <button type="button" className="btn" disabled={!csv || run.isPending} onClick={() => run.mutate(true)}>
              {run.isPending ? 'Wird geprüft …' : 'Prüfen'}
            </button>
          )}
          {result?.dryRun && (
            <button type="button" className="btn" disabled={run.isPending || result.created + result.updated === 0} onClick={() => run.mutate(false)}>
              {run.isPending ? 'Wird importiert …' : 'Jetzt importieren'}
            </button>
          )}
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {result ? (
        <>
          <Notice kind={result.dryRun ? 'info' : 'success'}>
            {result.dryRun ? 'Vorschau, noch nichts gespeichert.' : 'Import abgeschlossen.'} {result.rows} Zeilen gelesen.
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
              <li>{n(result.created)} {result.dryRun ? 'würden neu angelegt' : 'neu angelegt'}</li>
              <li>{n(result.updated)} {result.dryRun ? 'würden geändert' : 'geändert'}</li>
              {result.unchanged > 0 && <li>{n(result.unchanged)} schon aktuell</li>}
            </ul>
          </Notice>
          {(result.changes ?? []).length > 0 && (
            <details className="small" style={{ marginBottom: 12 }}>
              <summary>Was sich ändert</summary>
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {result.changes!.slice(0, 50).map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </details>
          )}
          {result.warnings.length > 0 && (
            <Notice kind="warn">
              <strong>{result.warnings.length} Hinweise:</strong>
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {result.warnings.slice(0, 12).map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
              {result.warnings.length > 12 && <div>und {result.warnings.length - 12} weitere</div>}
            </Notice>
          )}
        </>
      ) : (
        <>
          <div className="small" style={{ marginTop: 0 }}>
            {help}
          </div>
          <Field label="Excel- oder CSV-Datei" hint={reading ? 'Datei wird gelesen …' : fileName ? `${fileName} geladen` : undefined}>
            <input type="file" accept={TABLE_ACCEPT} onChange={(e) => void pick(e.target.files?.[0])} />
          </Field>
        </>
      )}
    </Modal>
  )
}
